import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mountain, Check, ArrowLeft, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DISCLAIMER } from "@/lib/constants";

const BENEFITS = [
    "Assistente de IA especializado em mineração e ANM",
    "Pesquisa de legislação mineral com fontes oficiais",
    "Gestão de processos, prazos e alertas automáticos",
    "Checklists profissionais e central de documentos",
];

const PLAN_META = {
    essencial: { tagline: "Para o profissional autônomo", highlight: false },
    avancado: { tagline: "Para escritórios em crescimento", highlight: true },
    consultoria: { tagline: "Para consultorias e equipes", highlight: false },
};

export default function Subscribe() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [busy, setBusy] = useState("");
    const [plans, setPlans] = useState([]);
    const [coupon, setCoupon] = useState("");
    const [applied, setApplied] = useState(null); // {code, discount_type, value, label}
    const [couponBusy, setCouponBusy] = useState(false);
    const [preselected, setPreselected] = useState("");
    const [guestOpen, setGuestOpen] = useState(false);
    const [guestPlan, setGuestPlan] = useState("");
    const [gName, setGName] = useState("");
    const [gEmail, setGEmail] = useState("");
    const [gCpf, setGCpf] = useState("");
    const [guestBusy, setGuestBusy] = useState(false);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get("cancelled")) toast.info("Pagamento cancelado.");
        const p = params.get("plan");
        if (p && PLAN_META[p]) setPreselected(p);
        api.get("/plans").then((r) => setPlans(r.data.all_plans || [])).catch(() => {});
    }, []);

    // Scroll to the plan preselected from the landing page so the user can review the price + coupon
    useEffect(() => {
        if (!preselected || plans.length === 0) return;
        const el = document.querySelector(`[data-testid="subscribe-plan-${preselected}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, [preselected, plans]);

    const brl = (v) => `R$ ${Number(v || 0).toLocaleString("pt-BR")}`;

    const discountedPrice = (price) => {
        if (!applied) return null;
        const d = applied.discount_type === "percent"
            ? price * (1 - applied.value / 100)
            : Math.max(0, price - applied.value);
        return Math.round(d * 100) / 100;
    };

    const applyCoupon = async () => {
        const code = coupon.trim();
        if (!code) return;
        setCouponBusy(true);
        try {
            const planKey = (plans[0] && plans[0].key) || "essencial";
            const r = await api.post("/vouchers/validate", { code, plan: planKey });
            setApplied({ code: r.data.code, discount_type: r.data.discount_type, value: r.data.value, label: r.data.label, duration: r.data.duration });
            toast.success(`Cupom aplicado: ${r.data.label} (${r.data.duration}).`);
        } catch (e) {
            setApplied(null);
            toast.error(e.response?.data?.detail || "Cupom inválido.");
        } finally { setCouponBusy(false); }
    };
    const clearCoupon = () => { setApplied(null); setCoupon(""); };

    const subscribe = async (plan) => {
        if (!user) { setGuestPlan(plan); setGuestOpen(true); return; }
        setBusy(plan);
        try {
            const res = await api.post("/payments/checkout", { origin_url: window.location.origin, plan, voucher: applied ? applied.code : undefined });
            window.location.href = res.data.checkout_url;
        } catch (e) {
            toast.error(e.response?.data?.detail || "Não foi possível iniciar o pagamento.");
            setBusy("");
        }
    };

    const guestSubscribe = async () => {
        if (gName.trim().length < 3) { toast.error("Informe seu nome completo."); return; }
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(gEmail.trim())) { toast.error("Informe um e-mail válido."); return; }
        setGuestBusy(true);
        try {
            const res = await api.post("/payments/guest-checkout", {
                origin_url: window.location.origin, plan: guestPlan,
                voucher: applied ? applied.code : undefined,
                name: gName.trim(), email: gEmail.trim().toLowerCase(), cpf: gCpf.trim(),
            });
            window.location.href = res.data.checkout_url;
        } catch (e) {
            toast.error(e.response?.data?.detail || "Não foi possível iniciar o pagamento.");
            setGuestBusy(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 topo-texture">
            <header className="bg-white border-b border-slate-200">
                <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
                    <button onClick={() => navigate(user ? "/dashboard" : "/")} className="flex items-center gap-2 font-heading font-black text-lg text-petroleum" data-testid="subscribe-logo">
                        <Mountain className="w-6 h-6 text-mineral" /> GEOMINE MANAGER
                    </button>
                    {user && (
                        <button onClick={() => navigate("/dashboard")} className="text-sm text-slate-500 hover:text-petroleum flex items-center gap-1" data-testid="subscribe-back">
                            <ArrowLeft className="w-4 h-4" /> Voltar
                        </button>
                    )}
                </div>
            </header>

            <div className="max-w-6xl mx-auto px-6 py-14">
                <div className="text-center max-w-2xl mx-auto">
                    <h1 className="font-heading text-3xl md:text-4xl font-black text-slate-900 tracking-tight">Escolha seu plano Geomine Manager</h1>
                    <p className="mt-3 text-slate-600">Gerencie sua carteira mineral com economia de tempo real. Cobrança mensal recorrente, cancele quando quiser.</p>
                    {user && !loading && (
                        <div className="mt-4 inline-flex items-center gap-2 text-sm px-3 py-1 rounded-full bg-mineral/15 text-mineralHover border border-mineral/30" data-testid="subscribe-status">
                            Status atual: {user.subscription_status}
                        </div>
                    )}
                </div>

                <div className="mt-8 max-w-md mx-auto" data-testid="coupon-box">
                    <label className="block text-xs font-semibold text-slate-500 mb-1.5 text-center">Tem um cupom de desconto?</label>
                    <div className="flex gap-2">
                        <input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} disabled={!!applied}
                            placeholder="Digite o código do cupom" data-testid="coupon-input"
                            className="flex-1 px-4 py-2.5 text-sm bg-white border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-mineral/30 focus:border-mineral uppercase disabled:opacity-60" />
                        {applied ? (
                            <button onClick={clearCoupon} data-testid="coupon-clear" className="px-4 py-2.5 text-sm font-semibold rounded-sm border border-slate-300 text-slate-600 hover:bg-slate-50">Remover</button>
                        ) : (
                            <button onClick={applyCoupon} disabled={couponBusy || !coupon.trim()} data-testid="coupon-apply" className="px-5 py-2.5 text-sm font-bold rounded-sm bg-mineral hover:bg-mineralHover text-white disabled:opacity-60">
                                {couponBusy ? "..." : "Aplicar"}
                            </button>
                        )}
                    </div>
                    {applied && <p className="mt-2 text-center text-xs font-semibold text-green-700" data-testid="coupon-applied">✓ {applied.label} aplicado ({applied.duration}) — código {applied.code}</p>}
                </div>

                <div className="mt-8 grid md:grid-cols-3 gap-5 items-start">
                    {plans.map((p) => {
                        const meta = PLAN_META[p.key] || {};
                        const dp = discountedPrice(p.price);
                        return (
                            <div key={p.key} data-testid={`subscribe-plan-${p.key}`}
                                className={`rounded-2xl p-7 shadow-xl relative overflow-hidden transition-shadow ${meta.highlight ? "bg-darkblue text-white ring-2 ring-mineral md:-mt-3 md:mb-3" : "bg-white text-slate-900 border border-slate-200"} ${preselected === p.key ? "ring-4 ring-mineral ring-offset-2" : ""}`}>
                                {meta.highlight && <span className="absolute top-4 right-4 text-[11px] font-bold bg-mineral text-white px-2 py-0.5 rounded-full">MAIS POPULAR</span>}
                                <h3 className="font-heading text-xl font-black">{p.name}</h3>
                                <p className={`text-xs mt-1 ${meta.highlight ? "text-white/60" : "text-slate-500"}`}>{meta.tagline}</p>
                                <div className="mt-4 flex items-end gap-1 flex-wrap">
                                    {dp != null && dp !== p.price ? (
                                        <>
                                            <span className={`font-heading text-lg font-bold line-through ${meta.highlight ? "text-white/40" : "text-slate-400"}`}>{brl(p.price)}</span>
                                            <span className="font-heading text-4xl font-black text-mineral" data-testid={`plan-discounted-${p.key}`}>{brl(dp)}</span>
                                        </>
                                    ) : (
                                        <span className="font-heading text-4xl font-black">{brl(p.price)}</span>
                                    )}
                                    <span className={`mb-1 text-sm ${meta.highlight ? "text-white/60" : "text-slate-400"}`}>/mês</span>
                                </div>
                                {dp != null && dp !== p.price && <p className={`text-[11px] mt-1 ${meta.highlight ? "text-white/60" : "text-slate-500"}`}>com cupom {applied.code} · {applied.duration}</p>}
                                <ul className={`mt-5 space-y-2 text-sm ${meta.highlight ? "text-white/90" : "text-slate-700"}`}>
                                    <li className="flex gap-2"><Check className="w-4 h-4 text-mineral shrink-0 mt-0.5" /> {p.process_limit} processos ANM</li>
                                    <li className="flex gap-2"><Check className="w-4 h-4 text-mineral shrink-0 mt-0.5" /> {p.consultation_limit} consultas IA/mês</li>
                                    <li className="flex gap-2"><Check className="w-4 h-4 text-mineral shrink-0 mt-0.5" /> {(p.token_limit / 1000).toLocaleString("pt-BR")} mil tokens/mês</li>
                                    <li className="flex gap-2"><Check className="w-4 h-4 text-mineral shrink-0 mt-0.5" /> {p.user_limit} usuário(s)</li>
                                </ul>
                                <button onClick={() => subscribe(p.key)} disabled={!!busy} data-testid={`subscribe-checkout-${p.key}`}
                                    className={`mt-7 w-full rounded-sm py-3.5 font-bold shadow-lg transition-colors disabled:opacity-60 ${meta.highlight ? "bg-mineral hover:bg-mineralHover text-white" : "bg-petroleum hover:bg-petroleumHover text-white"}`}>
                                    {busy === p.key ? "Redirecionando..." : "Assinar"}
                                </button>
                            </div>
                        );
                    })}
                </div>

                <div className="mt-10 max-w-2xl mx-auto">
                    <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-slate-600">
                        {BENEFITS.map((b) => (<span key={b} className="flex items-center gap-1.5"><Check className="w-4 h-4 text-mineral" /> {b}</span>))}
                    </div>
                    <p className="mt-3 text-center text-xs text-slate-400">Precisa de mais processos? Adicione Processos Adicionais (R$ 199/mês cada) a qualquer plano depois de assinar.</p>
                </div>

                <div className="mt-8 max-w-lg mx-auto flex items-start gap-2 text-xs text-slate-400">
                    <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" /> {DISCLAIMER}
                </div>
            </div>

            {guestOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" data-testid="guest-modal">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
                        <h3 className="font-heading text-xl font-black text-slate-900">Seus dados para assinar</h3>
                        <p className="mt-1 text-sm text-slate-500">Informe seus dados para continuar para o pagamento seguro no Mercado Pago. Depois do pagamento você define sua senha.</p>
                        <div className="mt-4 space-y-3">
                            <input value={gName} onChange={(e) => setGName(e.target.value)} data-testid="guest-name"
                                placeholder="Nome completo" className="w-full px-4 py-2.5 text-sm bg-white border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-mineral/30 focus:border-mineral" />
                            <input value={gEmail} onChange={(e) => setGEmail(e.target.value)} data-testid="guest-email" type="email"
                                placeholder="E-mail" className="w-full px-4 py-2.5 text-sm bg-white border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-mineral/30 focus:border-mineral" />
                            <input value={gCpf} onChange={(e) => setGCpf(e.target.value)} data-testid="guest-cpf"
                                placeholder="CPF (opcional)" className="w-full px-4 py-2.5 text-sm bg-white border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-mineral/30 focus:border-mineral" />
                        </div>
                        {applied && <p className="mt-3 text-center text-xs font-semibold text-green-700">✓ Cupom {applied.code} será aplicado</p>}
                        <div className="mt-5 flex gap-2">
                            <button onClick={() => { setGuestOpen(false); setGuestBusy(false); }} data-testid="guest-cancel"
                                className="flex-1 py-2.5 text-sm font-semibold rounded-sm border border-slate-300 text-slate-600 hover:bg-slate-50">Cancelar</button>
                            <button onClick={guestSubscribe} disabled={guestBusy} data-testid="guest-continue"
                                className="flex-1 py-2.5 text-sm font-bold rounded-sm bg-mineral hover:bg-mineralHover text-white disabled:opacity-60">
                                {guestBusy ? "Redirecionando..." : "Ir para o pagamento"}
                            </button>
                        </div>
                        <p className="mt-3 text-[11px] text-center text-slate-400">Já tem conta? <button onClick={() => navigate("/login")} className="text-mineral font-semibold hover:underline">Entrar</button></p>
                    </div>
                </div>
            )}
        </div>
    );
}
